import React, {useEffect} from 'react';
import {Link} from 'react-router';
import {useSelector} from 'react-redux';
import useGet from "../../hooks/useGet";
import {setIsDashboardShown} from "../../actions/locationActions";
import {getExternalDashboardUrl, isDevelopmentMode} from "../../utils/configUtils";
import {
    getBoardingTileText, getCCTileText,
    getCovidTileText,
    getPassportInformationTileText,
    getTravelTileText,
    getLuggageTileText,
    getMedicalAndMobilityTileText,
    getImmigrationTileText,
} from "../../utils/dashboardUtils";
import { getDashboardTitleText, showUPTInfoBox, getBackToDashboardText } from "../../utils/themeUtils";
import BackLink from "../../components/BackLink";
import DashboardTile from "../../components/DashboardTile";
import InfoBox from "../../components/passengerInformation/InfoBox";
import { useLingui } from '@lingui/react/macro';

function Dashboard() {
    useEffect(() => {
        setIsDashboardShown(true);

        return () =>  {
            setIsDashboardShown(false);
        }
    }, []);

    const authentication = useSelector(state => state.authentication);

    const {
        apiStatus,
        travelInfoOverAllStatus,
        covidSurveyOverallStatus,
        cardRegistrationOverAllStatus,
        showPreTravelTile,
        preTravelSubmissionOpen,
        preTravelSubmissionElapsed,
        showInHousePassportInfoTile,
        inHousePassportInfoTileAccessOpen,
        inHousePassportInfoTileAccessElapsed,
        showCovidSurveyTile,
        covidSurveySubmissionOpen,
        covidSurveySubmissionElapsed,
        showBoardingPassTile,
        boardingPassAccessOpen,
        showLuggageTagsTile,
        luggageTagAccessOpen,
        luggageTagAccessElapsed,
        covidPositive,
        showCardRegistrationTile,
        cardRegistrationOpen,
        cardRegistrationElapsed,
        passengers,
        medicalMobilityOptedIn,
        showPrerequisiteQuestionnaireTile,
        prerequisiteQuestionnaireOverAllStatus,
        prerequisiteQuestionnaireSubmissionOpen,
        prerequisiteQuestionnaireSubmissionElapsed,
        showPreflightQuestionnaireTile,
        immigrationApplicable,
        preflightQuestionnaireOverAllStatus,
        preflightQuestionnaireSubmissionOpen,
        preflightQuestionnaireSubmissionElapsed,
        config
    } = useGet('precruise/status', {});

    const {
        medicalMobilityOpenCutOff,
        passportOpenCutOff,
        travelinfoOpenCutOff,
        covidOpenCutOff,
        cardRegistrationOpenCutOff,
        immigrationOpenCutOff,
        luggageTagsDownloadOpenCutOff,
    } = config || {};


    const {t} = useLingui();

    // TODO - extract this variables to come from helper functions once requirements solidify
    const selectedPassenger = passengers?.find(passenger => passenger.selected);
    const computedTravelInfoStatus = selectedPassenger?.travelInfoStatus || travelInfoOverAllStatus;
    const computedCardStatus = selectedPassenger?.cardRegistrationStatus || cardRegistrationOverAllStatus;

    if(!travelInfoOverAllStatus) {
        return null;
    }

    const externalDashboardUrl = getExternalDashboardUrl();
    const tradePortalBookingUrl = authentication?.tradeportal?.bookingUrl || "";
    const backUrl = tradePortalBookingUrl || externalDashboardUrl;


    return (
        <div>
            <div className="container mx-auto">
                <h1>{getDashboardTitleText()}</h1>
                {backUrl &&
                <div className="mt-4">
                    <BackLink to={backUrl} isExternal={true} text={getBackToDashboardText(!!tradePortalBookingUrl)}/>
                </div>}
            </div>

            <div className="bg-primary-lightest-25 py-8 my-8 shadow">
                <div className="container mx-auto">
                    {showUPTInfoBox() && apiStatus !== "COMPLETE" &&
                    <InfoBox
                        classes="my-4"
                        infoText={<span className="font-bold text-primary-dark">{t({id: "universal_permission_to_travel"})}</span>}
                        description={<>{t({id: "legal_requirement_text"})}</>}
                    />
                    }
                    <div className="grid gap-x-4 gap-y-6 sm:grid-cols-2 xl:grid-cols-3">
                            {medicalMobilityOptedIn &&
                            <DashboardTile
                                linkTo={ t({id: "/precruise/passengers"}) + "/medicalAndMobility"}
                                icon="medical"
                                title={t({id: "medical_and_mobility"})}
                                status={prerequisiteQuestionnaireOverAllStatus}
                                description={getMedicalAndMobilityTileText(prerequisiteQuestionnaireOverAllStatus, prerequisiteQuestionnaireSubmissionOpen, prerequisiteQuestionnaireSubmissionElapsed, medicalMobilityOpenCutOff)}
                                disabled={!showPrerequisiteQuestionnaireTile}
                            />
                            }

                            {showInHousePassportInfoTile !== undefined &&
                            <DashboardTile
                                linkTo={t({id: "/precruise/passportDashboard"})}
                                icon="visa"
                                title={t({id: "passport_information"})}
                                status={apiStatus}
                                description={getPassportInformationTileText(apiStatus, inHousePassportInfoTileAccessOpen, inHousePassportInfoTileAccessElapsed, medicalMobilityOptedIn, prerequisiteQuestionnaireOverAllStatus, passportOpenCutOff)}
                                disabled={!showInHousePassportInfoTile}
                            />
                            }

                            {showPreTravelTile !== undefined &&
                            <DashboardTile
                                linkTo={t({id: "/precruise/passengers"})}
                                icon="family"
                                title={t({id: "travel_information"})}
                                description={getTravelTileText(computedTravelInfoStatus, preTravelSubmissionOpen, preTravelSubmissionElapsed, medicalMobilityOptedIn, prerequisiteQuestionnaireOverAllStatus, apiStatus, inHousePassportInfoTileAccessOpen, travelinfoOpenCutOff)}
                                status={computedTravelInfoStatus}
                                disabled={!showPreTravelTile}
                            />
                            }
                            
                            {showCovidSurveyTile !== undefined &&
                            <DashboardTile
                                linkTo={t({id: "/precruise/covidSurvey"})}
                                icon="heartbeat"
                                title={t({id: "health_survey"})}
                                description={getCovidTileText(covidSurveyOverallStatus, covidSurveySubmissionOpen, covidSurveySubmissionElapsed, covidPositive, apiStatus, inHousePassportInfoTileAccessOpen, covidOpenCutOff)}
                                status={covidPositive ? "" : covidSurveyOverallStatus}
                                disabled={!showCovidSurveyTile}
                            />
                            }

                            {showCardRegistrationTile !== undefined &&
                            <DashboardTile
                                linkTo={ t({id: "/precruise/passengers"}) + "/cc"}
                                icon="cc"
                                title={t({id: "card_registration"})}
                                description={getCCTileText(computedCardStatus, cardRegistrationOpen, cardRegistrationElapsed, apiStatus, inHousePassportInfoTileAccessOpen, cardRegistrationOpenCutOff)}
                                status={computedCardStatus}
                                disabled={!showCardRegistrationTile}
                            />
                            }

                            {immigrationApplicable && 
                                <DashboardTile
                                    linkTo={ t({id: "/precruise/immigrationCheck"})}
                                    icon="immigration"
                                    title={t({id: "immigration_check"})}
                                    description={getImmigrationTileText(preflightQuestionnaireOverAllStatus, preflightQuestionnaireSubmissionOpen, preflightQuestionnaireSubmissionElapsed, covidPositive, apiStatus, inHousePassportInfoTileAccessOpen, immigrationOpenCutOff)}
                                    status={preflightQuestionnaireOverAllStatus}
                                    disabled={!showPreflightQuestionnaireTile}
                                />
                            }

                            {showBoardingPassTile !== undefined &&
                            <DashboardTile
                                linkTo={t({id: "/boarding"})}
                                icon="passport"
                                title={t({id: "boarding_passes"})}
                                description={getBoardingTileText(
                                    travelInfoOverAllStatus,
                                    covidSurveyOverallStatus,
                                    cardRegistrationOverAllStatus,
                                    medicalMobilityOptedIn,
                                    prerequisiteQuestionnaireOverAllStatus,
                                    apiStatus,
                                    immigrationApplicable,
                                    preflightQuestionnaireOverAllStatus,
                                    covidPositive,
                                    boardingPassAccessOpen,
                                    covidSurveySubmissionElapsed
                                )}
                                disabled={!showBoardingPassTile}
                            />
                            }

                            {showLuggageTagsTile !== undefined &&
                            <DashboardTile
                                linkTo={t({id: "/luggage"})}
                                icon="pricetag"
                                title={t({id: "luggage_tags"})}
                                description={getLuggageTileText(luggageTagAccessOpen, luggageTagAccessElapsed, apiStatus, inHousePassportInfoTileAccessOpen, luggageTagsDownloadOpenCutOff)}
                                disabled={!showLuggageTagsTile}
                            />
                            }
                    </div>

                    {isDevelopmentMode() && <DevDashboardLinks/>}
                </div>
            </div>
        </div>
    );
}

export default Dashboard;


function DevDashboardLinks() {
    const {t} = useLingui();
    return <div className="my-8">
        <Link className="block mt-2" to={ t({id: "/precruise/passengers"}) + "/medicalAndMobility"}>Medical and Mobility</Link>
        <Link className="block mt-2" to={t({id: "/precruise/passengers"})}>Travel info</Link>
        <Link className="block mt-2" to={t({id: "/itinerary"})}>Cruise planner</Link>
        <Link className="block mt-2" to={t({id: "/precruise/passportDashboard"})}>in house apis (for 3rd party pax)</Link>
        <Link className="block mt-2" to={t({id: "/precruise/covidSurvey"})}>Health Survey</Link>
        <Link className="block mt-2" to={t({id: "/precruise/immigrationCheck"})}>Immigration</Link>
        <Link className="block mt-2" to={t({id: "/boarding"})}>Boarding passes</Link>
        <Link className="block mt-2" to={t({id: "/luggage"})}>Luggage tags</Link>
        <Link className="block mt-2" to={ t({id: "/precruise/passengers"}) + "/cc"}>Credit card</Link>
    </div>
}
